import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, ExternalLink, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { getSubscription, createPortalSession, cancelSubscription, resubscribe, cancelPendingChange } from '../../services/billingService';
import UsageMeter from '../../components/common/UsageMeter';
import PlanBadge from '../../components/common/PlanBadge';

export function BillingSection() {
  const { planId, isFreeTier, refreshSubscription } = useSubscription();
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getSubscription();
        setSubData(data);
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleManageBilling = async () => {
    setActionLoading('portal');
    try {
      const { url } = await createPortalSession();
      if (url) window.location.href = url;
    } catch (error) {
      console.error('Portal error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will keep access until the end of your billing period.')) {
      return;
    }
    setActionLoading('cancel');
    try {
      await cancelSubscription();
      await refreshSubscription();
      const data = await getSubscription();
      setSubData(data);
    } catch (error) {
      console.error('Cancel error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelPendingChange = async () => {
    setActionLoading('cancel-pending');
    try {
      await cancelPendingChange();
      await refreshSubscription();
      const data = await getSubscription();
      setSubData(data);
    } catch (error) {
      console.error('Cancel pending change error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResubscribe = async () => {
    setActionLoading('resubscribe');
    try {
      await resubscribe();
      await refreshSubscription();
      const data = await getSubscription();
      setSubData(data);
    } catch (error) {
      console.error('Resubscribe error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const subscription = subData?.subscription;
  const usage = subData?.usage || {};
  const plan = subData?.plan;

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Current Plan</h2>
          </div>
          <PlanBadge />
        </div>

        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Plan</span>
            <span className="text-foreground font-medium">{plan?.name || 'Free'}</span>
          </div>

          {subscription?.billing_interval && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Billing</span>
              <span className="text-foreground capitalize">{subscription.billing_interval}</span>
            </div>
          )}

          {subscription?.affiliate_code && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Affiliate Code</span>
              <span className="text-foreground font-medium text-green-400">{subscription.affiliate_code}</span>
            </div>
          )}

          {subscription?.discount_code && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Discount Code</span>
              <span className="text-foreground font-medium text-green-400">{subscription.discount_code}</span>
            </div>
          )}

          {subscription?.discount_amount_cents && subscription.discount_amount_cents > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Discount Applied</span>
              <span className="text-foreground font-medium text-green-400">${(subscription.discount_amount_cents / 100).toFixed(2)}</span>
            </div>
          )}

          {subscription?.status && subscription.status !== 'active' && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <span className={`font-medium capitalize ${
                subscription.status === 'past_due' ? 'text-yellow-400' :
                subscription.status === 'canceled' || subscription.status === 'expired' ? 'text-red-400' :
                'text-foreground'
              }`}>
                {subscription.status === 'past_due' ? 'Past Due' : subscription.status}
              </span>
            </div>
          )}

          {subscription?.current_period_end && !isFreeTier && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {subscription.cancel_at_period_end ? 'Access Until' : 'Next Billing Date'}
              </span>
              <span className="text-foreground">
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Status banners */}
        {subscription?.cancel_at_period_end && (
          <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-yellow-400 font-medium">Subscription canceling</p>
              <p className="text-muted-foreground">
                You'll keep access until {new Date(subscription.current_period_end).toLocaleDateString()}.
                After that, you'll be on the Free plan.
              </p>
            </div>
          </div>
        )}

        {subscription?.pending_plan_id && (
          <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm flex-1">
              <p className="text-blue-400 font-medium">Plan change scheduled</p>
              <p className="text-muted-foreground">
                Your plan will change to {subscription.pending_plan_name || subscription.pending_plan_id}{' '}
                ({subscription.pending_billing_interval}) on{' '}
                {new Date(subscription.current_period_end).toLocaleDateString()}.
              </p>
              <button
                onClick={handleCancelPendingChange}
                disabled={actionLoading === 'cancel-pending'}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline disabled:opacity-50"
              >
                {actionLoading === 'cancel-pending' ? 'Canceling...' : 'Cancel this change'}
              </button>
            </div>
          </div>
        )}

        {subscription?.status === 'past_due' && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-red-400 font-medium">Payment failed</p>
              <p className="text-muted-foreground">
                Please update your payment method to keep your subscription active.
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-6">
          {isFreeTier ? (
            <Link
              to="/pricing"
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Upgrade Plan
            </Link>
          ) : (
            <>
              <button
                onClick={handleManageBilling}
                disabled={actionLoading === 'portal'}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-secondary border border-border text-foreground rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'portal' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Manage Billing
              </button>

              <Link
                to="/pricing"
                className="px-4 py-2 text-sm font-medium bg-secondary border border-border text-foreground rounded-lg hover:bg-secondary/80 transition-colors"
              >
                Change Plan
              </Link>

              {subscription?.cancel_at_period_end ? (
                <button
                  onClick={handleResubscribe}
                  disabled={actionLoading === 'resubscribe'}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'resubscribe' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Resubscribe
                </button>
              ) : (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading === 'cancel'}
                  className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'cancel' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Usage Section */}
      {!isFreeTier && Object.keys(usage).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Usage</h2>
          <div className="space-y-4">
            {Object.entries(usage).map(([key]) => {
              const USAGE_LABELS = {
                journals_per_week: 'Chronicle Journal Sessions',
                ai_messages_per_month: 'Sage Messages',
              };
              return (
                <UsageMeter
                  key={key}
                  featureKey={key}
                  label={USAGE_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Free tier usage */}
      {isFreeTier && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Usage</h2>
          <div className="space-y-4">
            <UsageMeter featureKey="journals_per_week" label="Chronicle Journal Sessions" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function BillingPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-foreground mb-8">Billing & Subscription</h1>
        <BillingSection />
      </div>
    </div>
  );
}
